import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  ButtonComponent, FieldComponent, InputComponent, ModalComponent,
} from '@echospaces-web-monorepo/es-shared-ui';

/** ~ISO 639-1 alpha-2 with optional region (e.g. `de`, `en-US`). Permissive on purpose. */
const LANGUAGE_RE = /^[a-z]{2}(-[A-Z]{2})?$|^all$/;

/**
 * Upload a localized file for an existing content. The server treats
 * re-uploads of an already-existing language as new versions — re-using
 * this modal to "replace" the active file is supported and intentional.
 */
@Component({
  selector: 'es-add-language-modal',
  imports: [FormsModule, ModalComponent, ButtonComponent, FieldComponent, InputComponent],
  template: `
    <es-modal [open]="open" title="Add language variant" width="sm" (closed)="onCancel()">
      <div class="p-6 space-y-4">
        <es-field
          label="Language code"
          hint="ISO 639-1 (e.g. de, en, fr) — use 'all' for the default fallback."
          [error]="languageError()"
        >
          <es-input placeholder="de" [(ngModel)]="language"></es-input>
        </es-field>

        <es-field label="File" [required]="true" [error]="fileError()">
          <input
            type="file"
            class="w-full text-sm text-[var(--text-secondary)] file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-echo-blue/20 file:text-echo-blue hover:file:bg-echo-blue/30 file:cursor-pointer"
            (change)="onFile($event)"
          />
        </es-field>

        @if (submitError()) {
          <p class="text-sm text-red-400">{{ submitError() }}</p>
        }

        <div class="flex justify-end gap-2 pt-2">
          <es-button variant="ghost" (clicked)="onCancel()">Cancel</es-button>
          <es-button
            variant="primary"
            [disabled]="submitting() || !pickedFile"
            (clicked)="onSubmit()"
          >
            {{ submitting() ? 'Uploading…' : 'Upload' }}
          </es-button>
        </div>
      </div>
    </es-modal>
  `,
})
export class AddLanguageModalComponent {
  @Input() open = false;
  /** Pre-fill the language field — used by the "Replace file" UX. */
  @Input() set initialLanguage(value: string | null | undefined) {
    if (value) this.language = value;
  }

  @Output() closed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<{ language: string; file: File }>();

  language = '';
  pickedFile: File | null = null;

  readonly submitting = signal(false);
  readonly languageError = signal<string | null>(null);
  readonly fileError = signal<string | null>(null);
  readonly submitError = signal<string | null>(null);

  onFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.pickedFile = input.files?.[0] ?? null;
    this.fileError.set(null);
  }

  onSubmit(): void {
    const lang = this.language.trim();
    if (!LANGUAGE_RE.test(lang)) {
      this.languageError.set('Must be a 2-letter ISO code (de, en) or "all"');
      return;
    }
    if (!this.pickedFile) {
      this.fileError.set('Pick a file to upload');
      return;
    }
    this.languageError.set(null);
    this.fileError.set(null);
    this.submitting.set(true);
    this.submitted.emit({ language: lang, file: this.pickedFile });
  }

  finishSubmit(error?: string): void {
    this.submitting.set(false);
    this.submitError.set(error ?? null);
    if (!error) {
      this.language = '';
      this.pickedFile = null;
      this.closed.emit();
    }
  }

  onCancel(): void {
    this.languageError.set(null);
    this.fileError.set(null);
    this.submitError.set(null);
    this.closed.emit();
  }
}
